import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { CheckCircle, Zap, Star, ArrowRight, Loader2, CreditCard, Building2, Timer, Wallet } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import API from "@/lib/api";
import { toast } from "sonner";

const DONUT_COLORS = ["bg-[#0c1e41]", "bg-emerald-600", "bg-amber-500", "bg-violet-600", "bg-rose-600"];

export default function Subscribe() {
  const navigate = useNavigate();
  const [plans, setPlans] = useState([]);
  const [currentSub, setCurrentSub] = useState(null);
  const [trialDaysLeft, setTrialDaysLeft] = useState(null);
  const [billing, setBilling] = useState("monthly");
  const [loading, setLoading] = useState(true);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [payMethod, setPayMethod] = useState("bank_transfer");
  const [payRef, setPayRef] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [gatewaySettings, setGatewaySettings] = useState({ paystack: { enabled: false }, flutterwave: { enabled: false } });

  useEffect(() => {
    Promise.all([
      API.get("/subscriptions/plans"),
      API.get("/subscriptions/current"),
      API.get("/payments/gateway-settings/public").catch(() => ({ data: {} })),
    ]).then(([p, s, gw]) => {
      setPlans(p.data.plans || []);
      setCurrentSub(s.data.subscription);
      setTrialDaysLeft(s.data.trial_days_remaining ?? null);
      if (gw.data) setGatewaySettings(gw.data);
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const price = (plan) => billing === "yearly" ? plan.price_yearly : plan.price_monthly;
  const credits = (plan) => billing === "yearly" ? plan.free_credits_yearly : plan.free_credits_monthly;
  const monthlyEquiv = (plan) => billing === "yearly" ? Math.round(plan.price_yearly / 12) : null;
  const fmtNaira = (kobo) => `₦${(kobo / 100).toLocaleString()}`;

  const availablePayMethods = [
    { id: "bank_transfer", Icon: Building2, label: "Bank Transfer", note: "5-day trial, admin confirms" },
    ...(gatewaySettings?.paystack?.enabled ? [{ id: "paystack", Icon: CreditCard, label: "Paystack", note: "Instant activation" }] : []),
    ...(gatewaySettings?.flutterwave?.enabled ? [{ id: "flutterwave", Icon: Wallet, label: "Flutterwave", note: "Instant activation" }] : []),
  ];

  const subscribe = async () => {
    if (!selectedPlan) return;
    if (payMethod === "bank_transfer") {
      if (!payRef.trim()) { toast.error("Enter your bank transfer reference"); return; }
      setSubmitting(true);
      try {
        const res = await API.post("/subscriptions/subscribe", {
          plan_id: selectedPlan._id,
          billing_cycle: billing,
          payment_method: "bank_transfer",
          payment_reference: payRef,
        });
        toast.success(res.data.message || "Trial started! Sender IDs are now unlocked.");
        navigate("/dashboard/sender-ids");
      } catch (e) {
        const detail = e?.response?.data?.detail || "";
        if (detail.includes("already have an active")) {
          toast.info("You already have an active subscription or ongoing trial.");
          navigate("/dashboard");
        } else { toast.error(detail || "Failed to submit subscription"); }
      } finally { setSubmitting(false); }
    } else if (payMethod === "paystack") {
      await payWithPaystack();
    } else if (payMethod === "flutterwave") {
      await payWithFlutterwave();
    }
  };

  const payWithPaystack = async () => {
    setSubmitting(true);
    try {
      const callbackUrl = `${window.location.origin}/payment/callback`;
      const { data } = await API.post("/payments/paystack/initialize-subscription", {
        plan_id: selectedPlan._id,
        billing_cycle: billing,
        callback_url: callbackUrl,
      });
      window.location.href = data.authorization_url;
    } catch (e) {
      const detail = e?.response?.data?.detail || "";
      if (detail.includes("already have an active")) {
        toast.info("You already have an active subscription or ongoing trial.");
        navigate("/dashboard");
      } else { toast.error(detail || "Failed to initialize Paystack payment"); }
      setSubmitting(false);
    }
  };

  const payWithFlutterwave = async () => {
    setSubmitting(true);
    try {
      const redirectUrl = `${window.location.origin}/payment/callback`;
      const { data } = await API.post("/payments/flutterwave/initialize-subscription", {
        plan_id: selectedPlan._id,
        billing_cycle: billing,
        redirect_url: redirectUrl,
      });
      window.location.href = data.checkout_url;
    } catch (e) {
      const detail = e?.response?.data?.detail || "";
      if (detail.includes("already have an active")) {
        toast.info("You already have an active subscription or ongoing trial.");
        navigate("/dashboard");
      } else { toast.error(detail || "Failed to initialize Flutterwave payment"); }
      setSubmitting(false);
    }
  };

  if (loading) return (
    <div className="flex items-center justify-center h-64">
      <Loader2 className="animate-spin text-[#0c1e41]" size={28} />
    </div>
  );

  return (
    <div className="max-w-5xl mx-auto space-y-8 pb-10" data-testid="subscribe-page">

      {/* Header */}
      <div className="text-center">
        <div className="inline-flex items-center gap-2 bg-[#eef1f8] border border-[#c0cfe8] text-[#0c1e41] text-xs font-bold px-4 py-1.5 rounded-full mb-3">
          <Zap size={12} /> Subscription Plans
        </div>
        <div className="inline-flex items-center gap-2 bg-blue-50 border border-blue-200 text-blue-700 text-xs font-semibold px-4 py-1.5 rounded-full mb-4 ml-2">
          <Timer size={11} /> 5-day free trial included with every plan
        </div>
        <h1 className="text-3xl sm:text-4xl font-black tracking-tight text-slate-900">Choose Your Plan</h1>
        <p className="text-slate-500 mt-3 text-base max-w-xl mx-auto">
          Every plan includes a <strong>5-day trial</strong> — Sender IDs are unlocked immediately. Free credits are added when admin confirms payment.
        </p>
      </div>

      {/* Current sub banner */}
      {currentSub && (
        <div className={`border rounded-2xl px-5 py-4 flex items-center gap-3 ${currentSub.status === "trial" ? "bg-blue-50 border-blue-200" : "bg-emerald-50 border-emerald-200"}`}>
          <CheckCircle size={18} className={`shrink-0 ${currentSub.status === "trial" ? "text-blue-500" : "text-emerald-600"}`} />
          <div>
            <p className={`text-sm font-bold ${currentSub.status === "trial" ? "text-blue-800" : "text-emerald-800"}`}>
              {currentSub.status === "trial" ? "Trial Active" : "Active"}: {currentSub.plan_name} ({currentSub.billing_cycle})
            </p>
            <p className={`text-xs mt-0.5 ${currentSub.status === "trial" ? "text-blue-600" : "text-emerald-600"}`}>
              {currentSub.status === "trial"
                ? `${trialDaysLeft === 0 ? "Expires today" : trialDaysLeft === 1 ? "1 day left" : `${trialDaysLeft} days left`} — complete bank transfer to activate fully`
                : `Renews ${currentSub.current_period_end ? new Date(currentSub.current_period_end).toLocaleDateString("en-NG") : "—"} · ${currentSub.sender_id_limit === -1 ? "Unlimited" : currentSub.sender_id_limit} Sender ID${currentSub.sender_id_limit !== 1 ? "s" : ""}`
              }
            </p>
          </div>
          <button className="ml-auto text-xs font-bold text-slate-500 hover:underline flex items-center gap-1" onClick={() => navigate("/dashboard")}>
            Dashboard <ArrowRight size={11} className="inline" />
          </button>
        </div>
      )}

      {/* Billing toggle */}
      <div className="flex items-center justify-center gap-3">
        <span className={`text-sm font-semibold ${billing === "monthly" ? "text-slate-900" : "text-slate-400"}`}>Monthly</span>
        <button
          onClick={() => setBilling(billing === "monthly" ? "yearly" : "monthly")}
          className={`w-12 h-6 rounded-full transition-colors relative ${billing === "yearly" ? "bg-[#0c1e41]" : "bg-slate-300"}`}
          data-testid="billing-toggle">
          <span className={`absolute top-1 w-4 h-4 bg-white rounded-full shadow transition-transform ${billing === "yearly" ? "translate-x-7" : "translate-x-1"}`} />
        </button>
        <span className={`text-sm font-semibold ${billing === "yearly" ? "text-slate-900" : "text-slate-400"}`}>
          Yearly <span className="text-emerald-600 font-bold text-xs ml-1">Save 20%</span>
        </span>
      </div>

      {/* Plans grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
        {plans.map((plan, idx) => {
          const isSelected = selectedPlan?._id === plan._id;
          const isFeatured = plan.is_featured;
          return (
            <div
              key={plan._id}
              data-testid={`plan-card-${plan.name.toLowerCase()}`}
              onClick={() => setSelectedPlan(isSelected ? null : plan)}
              className={`relative flex flex-col rounded-2xl p-5 cursor-pointer transition-all duration-200 border-2 ${
                isSelected
                  ? "border-[#0c1e41] bg-[#eef1f8] shadow-lg shadow-[#0c1e41]/10"
                  : isFeatured
                  ? "border-[#0c1e41] bg-white shadow-md"
                  : "border-slate-200 bg-white hover:border-[#0c1e41]/40 hover:shadow-sm"
              }`}>

              {isFeatured && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                  <span className="bg-[#0c1e41] text-white text-[10px] font-bold px-3 py-1 rounded-full flex items-center gap-1">
                    <Star size={9} fill="currentColor" /> Popular
                  </span>
                </div>
              )}

              {isSelected && (
                <div className="absolute top-3 right-3">
                  <CheckCircle size={18} className="text-[#0c1e41]" />
                </div>
              )}

              <div className={`w-8 h-8 rounded-xl ${DONUT_COLORS[idx % DONUT_COLORS.length]} flex items-center justify-center mb-3`}>
                <Zap size={15} className="text-white" />
              </div>

              <h3 className="text-sm font-extrabold text-slate-900 mb-1">{plan.name}</h3>
              <p className="text-xs text-slate-400 mb-4 leading-relaxed">{plan.description}</p>

              <div className="mb-1">
                <span className="text-2xl font-black text-slate-900">{fmtNaira(price(plan))}</span>
                <span className="text-xs text-slate-400">/{billing === "yearly" ? "yr" : "mo"}</span>
              </div>
              {billing === "yearly" && monthlyEquiv(plan) && (
                <p className="text-[10px] text-emerald-600 font-semibold mb-3">{fmtNaira(monthlyEquiv(plan))}/mo billed yearly</p>
              )}

              <div className="flex flex-col gap-1.5 mt-auto pt-4 border-t border-slate-100">
                <div className="flex items-center gap-2 text-xs text-slate-600">
                  <div className="w-4 h-4 bg-emerald-50 border border-emerald-200 rounded-full flex items-center justify-center shrink-0">
                    <CheckCircle size={9} className="text-emerald-600" />
                  </div>
                  {credits(plan).toLocaleString()} free credits
                </div>
                <div className="flex items-center gap-2 text-xs text-slate-600">
                  <div className="w-4 h-4 bg-emerald-50 border border-emerald-200 rounded-full flex items-center justify-center shrink-0">
                    <CheckCircle size={9} className="text-emerald-600" />
                  </div>
                  {plan.sender_id_limit === -1 ? "Unlimited" : plan.sender_id_limit} Sender ID{plan.sender_id_limit !== 1 ? "s" : ""}
                </div>
                <div className="flex items-center gap-2 text-xs text-blue-600">
                  <div className="w-4 h-4 bg-blue-50 border border-blue-200 rounded-full flex items-center justify-center shrink-0">
                    <Timer size={9} className="text-blue-600" />
                  </div>
                  5-day trial included
                </div>
                {(plan.features || []).slice(2).map((f, i) => (
                  <div key={i} className="flex items-center gap-2 text-xs text-slate-500">
                    <div className="w-4 h-4 bg-slate-50 border border-slate-200 rounded-full flex items-center justify-center shrink-0">
                      <CheckCircle size={9} className="text-slate-400" />
                    </div>
                    {f}
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>

      {/* Payment section */}
      {selectedPlan && (
        <div className="bg-white border-2 border-[#0c1e41] rounded-2xl p-6 shadow-lg shadow-[#0c1e41]/10 max-w-lg mx-auto" data-testid="payment-section">
          <h3 className="text-base font-extrabold text-slate-900 mb-1">
            Subscribe — {selectedPlan.name} ({billing})
          </h3>
          <p className="text-sm text-slate-500 mb-1">
            {fmtNaira(price(selectedPlan))} · {credits(selectedPlan).toLocaleString()} free credits · {selectedPlan.sender_id_limit === -1 ? "Unlimited" : selectedPlan.sender_id_limit} Sender ID{selectedPlan.sender_id_limit !== 1 ? "s" : ""}
          </p>
          <div className="inline-flex items-center gap-1 text-xs text-blue-700 bg-blue-50 border border-blue-200 px-2.5 py-1 rounded-full mb-5">
            <Timer size={10} /> Sender IDs unlocked immediately for 5 days (bank transfer) or instantly (online payment)
          </div>

          {/* Payment method tabs */}
          <div className="flex gap-1 bg-slate-100 rounded-xl p-1 mb-5">
            {availablePayMethods.map(({ id, Icon, label }) => (
              <button key={id} onClick={() => setPayMethod(id)}
                className={`flex-1 flex items-center justify-center gap-1.5 py-2 rounded-lg text-xs font-semibold transition-all ${payMethod === id ? "bg-white text-slate-900 shadow-sm" : "text-slate-500 hover:text-slate-700"}`}
                data-testid={`pay-method-${id}`}>
                <Icon size={13} /> {label}
              </button>
            ))}
          </div>

          {payMethod === "bank_transfer" && (
            <div className="space-y-4">
              <div className="bg-[#eef1f8] border border-[#c0cfe8] rounded-xl p-4 text-sm">
                <p className="font-bold text-[#0c1e41] mb-2">Transfer {fmtNaira(price(selectedPlan))} to:</p>
                <p className="text-slate-700 text-xs leading-relaxed">
                  Bank details are configured in <strong>Admin → Settings → Bank Transfer</strong>.<br />
                  Use your email as reference/narration.
                </p>
              </div>
              <div className="space-y-2">
                <Label className="text-slate-700 text-sm">Transfer Reference / Narration</Label>
                <Input value={payRef} onChange={(e) => setPayRef(e.target.value)} placeholder="e.g. your-email@example.com"
                  className="bg-white border-slate-300 text-slate-900 h-10 placeholder:text-slate-400" data-testid="pay-reference" />
              </div>
              <Button onClick={subscribe} disabled={submitting} className="w-full bg-[#0c1e41] hover:bg-[#091629] h-11 text-sm font-bold shadow-lg shadow-[#0c1e41]/20" data-testid="subscribe-submit">
                {submitting
                  ? <><Loader2 size={14} className="animate-spin mr-2" />Submitting...</>
                  : <>Start 5-Day Trial <ArrowRight size={14} className="ml-2" /></>}
              </Button>
            </div>
          )}

          {payMethod === "paystack" && (
            <div className="space-y-4">
              <div className="bg-[#eef1f8] border border-[#c0cfe8] rounded-xl p-5 text-center">
                <CreditCard size={28} className="text-[#0c1e41] mx-auto mb-3" />
                <p className="text-sm font-semibold text-slate-900 mb-1">Pay {fmtNaira(price(selectedPlan))} via Paystack</p>
                <p className="text-xs text-slate-500">Subscription activates instantly after payment. Card, Bank Transfer, USSD.</p>
              </div>
              <Button onClick={subscribe} disabled={submitting} className="w-full bg-[#0c1e41] hover:bg-[#091629] h-11 text-sm font-bold shadow-lg shadow-[#0c1e41]/20" data-testid="paystack-subscribe-btn">
                {submitting
                  ? <><Loader2 size={14} className="animate-spin mr-2" />Redirecting to Paystack...</>
                  : <>Pay {fmtNaira(price(selectedPlan))} with Paystack <ArrowRight size={14} className="ml-2" /></>}
              </Button>
              <p className="text-xs text-slate-400 text-center">Secured by Paystack · Instant activation</p>
            </div>
          )}

          {payMethod === "flutterwave" && (
            <div className="space-y-4">
              <div className="bg-[#eef1f8] border border-[#c0cfe8] rounded-xl p-5 text-center">
                <Wallet size={28} className="text-[#0c1e41] mx-auto mb-3" />
                <p className="text-sm font-semibold text-slate-900 mb-1">Pay {fmtNaira(price(selectedPlan))} via Flutterwave</p>
                <p className="text-xs text-slate-500">Subscription activates instantly after payment. Card, Bank Transfer, Mobile Money.</p>
              </div>
              <Button onClick={subscribe} disabled={submitting} className="w-full bg-[#0c1e41] hover:bg-[#091629] h-11 text-sm font-bold shadow-lg shadow-[#0c1e41]/20" data-testid="flutterwave-subscribe-btn">
                {submitting
                  ? <><Loader2 size={14} className="animate-spin mr-2" />Redirecting to Flutterwave...</>
                  : <>Pay {fmtNaira(price(selectedPlan))} with Flutterwave <ArrowRight size={14} className="ml-2" /></>}
              </Button>
              <p className="text-xs text-slate-400 text-center">Secured by Flutterwave · Instant activation</p>
            </div>
          )}
        </div>
      )}

      {!selectedPlan && (
        <p className="text-center text-sm text-slate-400">Select a plan above to continue</p>
      )}
    </div>
  );
}
